import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Moon, History, TrendingUp, TrendingDown, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import CryptoIcon from '../CryptoIcon';
import api from '../../services/api';
import { formatPair } from '../../utils/commodities';
import { fmtUsd } from '../../utils/format';

const HEARTBEAT_MS = 60000;

const sinceLabel = (iso) => {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${mins} min`;
  if (mins < 1440) return `${Math.round(mins / 60)} h`;
  return `${Math.round(mins / 1440)} day${mins >= 2880 ? 's' : ''}`;
};

const Stat = ({ label, value, cls = 'text-white', testid }) => (
  <div className="p-3 rounded-xl bg-gray-900/60 text-center">
    <p className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">{label}</p>
    <p className={`text-lg font-bold ${cls}`} data-testid={testid}>{value}</p>
  </div>
);

const TradeLine = ({ t }) => (
  <div className="flex items-center gap-3 py-2" data-testid={`away-trade-${t.id}`}>
    {t.kind === 'binary' ? <CryptoIcon symbol={t.symbol} size={26} /> : <Sparkles className="h-6 w-6 text-teal-300" />}
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold truncate flex items-center gap-1.5">
        {t.kind === 'binary' ? formatPair(t.symbol) : (t.package || 'AI Arbitrage')}
        {t.prediction && (t.prediction === 'up' ? <TrendingUp className="h-3.5 w-3.5 text-emerald-400" /> : <TrendingDown className="h-3.5 w-3.5 text-red-400" />)}
      </p>
      <p className="text-xs text-gray-500">{fmtUsd(t.amount)} · {new Date(t.end_time).toLocaleString()}</p>
    </div>
    <span className={`text-sm font-bold ${t.profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{t.profit >= 0 ? '+' : ''}{fmtUsd(t.profit)}</span>
  </div>
);

// On app load: summarise trades that ended while the user was away, then keep a presence heartbeat.
export const AwaySummary = () => {
  const [summary, setSummary] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let iv;
    const beat = () => api.post('/presence/heartbeat').catch(() => {});
    const onHide = () => { if (document.visibilityState === 'hidden') beat(); };
    (async () => {
      const { data } = await api.get('/presence/away-summary').catch(() => ({ data: null }));
      if (data?.count > 0) setSummary(data);
      beat();
      iv = setInterval(beat, HEARTBEAT_MS);
    })();
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('phantomtrades:trade-settled', beat);
    return () => {
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('phantomtrades:trade-settled', beat);
    };
  }, []);

  if (!summary) return null;
  const net = summary.net_profit;
  const close = () => setSummary(null);
  return (
    <Dialog open onOpenChange={(o) => !o && close()}>
      <DialogContent className="!bg-[#0b1120] text-white sm:max-w-lg border border-purple-500/40" data-testid="away-summary-dialog">
        <DialogHeader className="!bg-transparent">
          <DialogTitle className="flex items-center gap-2 text-2xl"><Moon className="h-6 w-6 text-purple-300" /> While you were away</DialogTitle>
          <DialogDescription className="text-gray-400">
            {summary.count} trade{summary.count > 1 ? 's' : ''} finished in the last {sinceLabel(summary.since)}.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
          <Stat label="Wins" value={summary.wins} cls="text-emerald-400" testid="away-summary-wins" />
          <Stat label="Losses" value={summary.losses} cls="text-red-400" testid="away-summary-losses" />
          <Stat label="Net P/L" value={`${net >= 0 ? '+' : ''}${fmtUsd(net)}`} cls={net >= 0 ? 'text-emerald-400' : 'text-red-400'} testid="away-summary-net" />
        </div>
        <div className="max-h-64 overflow-y-auto divide-y divide-gray-800 !bg-transparent">
          {summary.trades.map((t) => <TradeLine key={t.id} t={t} />)}
        </div>
        {summary.count > summary.trades.length && (
          <p className="text-xs text-gray-500 text-center">+{summary.count - summary.trades.length} more in History</p>
        )}
        <div className="flex gap-3">
          <button type="button" onClick={close} data-testid="away-summary-close-btn" className="flex-1 py-3 rounded-xl bg-gray-800 hover:bg-gray-700 font-semibold transition-colors">Dismiss</button>
          <button type="button" onClick={() => { close(); navigate('/trading?tab=history'); window.dispatchEvent(new Event('phantomtrades:open-history')); }} data-testid="away-summary-view-history-btn" className="flex-1 py-3 rounded-xl bg-purple-600 hover:bg-purple-500 font-semibold flex items-center justify-center gap-2 transition-colors">
            <History className="h-4 w-4" /> View in History
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AwaySummary;
